import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import {
  Button,
  FormField,
  Input,
  Textarea,
} from "@/design-system/components/ui";
import { adminNotificationsApi } from "@/api/adminNotifications";
import { extractErrorMessage } from "@/api/client";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";

// Portal-absorption §2.5 — in-place targeted compose. Sends ONE notification
// to THIS portal account (audienceType Individual) via the existing admin
// notifications API; broadcast authoring stays on /admin/notifications/new.

export interface NotificationComposeModalProps {
  open: boolean;
  onClose: () => void;
  portalUserId: string;
  accountName?: string;
}

interface QuickTemplate {
  id: string;
  label: string;
  title: string;
  body: string;
}

// FE-local canned snippets — quick-pick prefills only, always editable.
const QUICK_TEMPLATES: QuickTemplate[] = [
  {
    id: "document-reminder",
    label: "Document reminder",
    title: "Document reminder",
    body:
      "We're still waiting on one or more documents from you. " +
      "Please sign in to your portal and upload the outstanding items when you have a moment.",
  },
  {
    id: "action-required",
    label: "Action required",
    title: "Action required on your account",
    body:
      "There's an item on your account that needs your attention. " +
      "Please sign in to your portal to review and complete it.",
  },
  {
    id: "general-update",
    label: "General update",
    title: "An update on your account",
    body:
      "We wanted to share a quick update about your account. " +
      "Please sign in to your portal for the details.",
  },
];

export function NotificationComposeModal({
  open,
  onClose,
  portalUserId,
  accountName,
}: NotificationComposeModalProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const toast = useToast();
  const queryClient = useQueryClient();

  const send = useMutation({
    mutationFn: () =>
      adminNotificationsApi.create({
        title: title.trim(),
        body: body.trim(),
        audienceType: "Individual",
        accountIds: [portalUserId],
        sendEmail,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: portalAbsorptionKeys.accountNotifications(portalUserId),
      });
      toast.success("Notification sent.");
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send a notification"
      description={
        accountName
          ? `Delivered to ${accountName}'s portal feed only.`
          : "Delivered to this account's portal feed only."
      }
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={send.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => send.mutate()}
            loading={send.isPending}
            disabled={!title.trim() || !body.trim()}
          >
            Send
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="flex flex-col gap-space-2">
          <span className="text-label text-gray-mid">Quick templates</span>
          <div className="flex flex-wrap gap-space-2" role="group" aria-label="Quick templates">
            {QUICK_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTitle(t.title);
                  setBody(t.body);
                }}
                className="rounded-pill border border-border-light bg-white-pure px-space-3 py-[5px] text-caption font-medium text-gray-mid transition-colors duration-fast ease-standard hover:border-gold hover:text-onyx focus-visible:outline-none focus-visible:shadow-goldRing"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <FormField label="Subject" required htmlFor="compose-subject">
          <Input
            id="compose-subject"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Subject"
          />
        </FormField>
        <FormField label="Message" required htmlFor="compose-message">
          <Textarea
            id="compose-message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="Message"
          />
        </FormField>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="accent-gold"
          />
          Also send as email
        </label>
        {send.isError && (
          <p className="text-caption text-status-error" role="alert">
            {extractErrorMessage(send.error)}
          </p>
        )}
      </div>
    </Modal>
  );
}
